import { useState } from "react";
import { Grid, Modal } from "@mui/material";
import { useSnackbar } from "notistack";
import { createTaskPayload, verfiyTask } from "../../functions/postData";
import {
  updateTaskAnswers,
  updateTaskQuestion,
  updateTaskRecommendations,
  updateTaskSettings,
  updateTaskSolution,
} from "../../functions/patchData";
import { showNotification } from "../../functions/snackbar";
import { showWarningSwal } from "../../functions/alert";
import { afterEdit, populateTask } from "../../functions/edit";
import { getTasks } from "../../functions/getData";
import ReusableButton from "../../components/general/ReusableButton";
import Loader from "../../components/general/Loader";
import NoData from "../../components/general/NoData";
import ConfirmVerfiy from "../../components/modal/ConfrimVerfiy";
import { TASK_MODE } from "./useTaskMode";
import { createDefaultTask } from "./taskDefaults";
import { buildTaskPayload, getTaskPayloadValidationError } from "./buildTaskPayload";
import {
  TASK_SECTION,
  getTaskSectionValidationError,
} from "./taskSectionEdit";
import TaskPanel from "./TaskPanel";

export default function TaskForm({ mode, isReadOnly, isVerify, editData }) {
  const isAdd = mode === TASK_MODE.ADD;

  const [addTasks, setAddTasks] = useState([createDefaultTask()]);
  const [confirmVerify, setConfirmVerify] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submittingSection, setSubmittingSection] = useState(null);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const {
    loading,
    firstLoaded,
    original,
    setOriginal,
    task: editTask,
    setTask: setEditTask,
    editAnswers,
  } = editData || {};

  const tasks = isAdd ? addTasks : editTask ? [editTask] : [];

  const setTaskAtIndex = (index, newTask) => {
    if (isAdd) {
      setAddTasks((prev) => {
        const next = [...prev];
        next[index] = newTask;
        return next;
      });
    } else {
      setEditTask(newTask);
    }
  };

  const handleAddTask = (atIndex) => {
    setAddTasks((prev) => {
      const next = [...prev];
      next.splice(atIndex, 0, createDefaultTask());
      return next;
    });
  };

  const handleDeleteTask = (atIndex) => {
    setAddTasks((prev) => prev.filter((_, i) => i !== atIndex));
  };

  const handleDuplicateTask = (atIndex, task) => {
    setAddTasks((prev) => {
      const next = [...prev];
      const copy = structuredClone(task);
      copy.id = null;
      copy.identifier = "";
      next.splice(atIndex, 0, copy);
      return next;
    });
  };

  const onSubmit = async () => {
    const errors = [];
    tasks.forEach((task, i) => {
      const err = getTaskPayloadValidationError(task);
      if (err) errors.push(`Task ${i + 1}: ${err}`);
    });
    if (errors.length) {
      return showWarningSwal(errors.join("<br>"));
    }

    setSubmitting(true);
    try {
      if (isAdd) {
        let lastMessage = "Tasks saved.";
        for (let i = 0; i < tasks.length; i++) {
          const payload = buildTaskPayload(tasks[i]);
          const { data: resBody, status: httpStatus } =
            await createTaskPayload(payload);
          if (![200, 201, 202, 204].includes(Number(httpStatus))) {
            showWarningSwal(
              resBody?.message || `Could not save task ${i + 1}.`
            );
            return;
          }
          if (resBody?.message) lastMessage = resBody.message;
        }
        showNotification(
          "success",
          lastMessage,
          enqueueSnackbar,
          closeSnackbar
        );
      }
    } catch (e) {
      // Errors are surfaced by http helpers / Swal in most cases
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmitSection = async (section) => {
    const task = tasks[0];
    if (!task?.id) {
      showWarningSwal("Missing task id.");
      return;
    }
    const err = getTaskSectionValidationError(task, section);
    if (err) return showWarningSwal(err);

    setSubmittingSection(section);
    try {
      let response;
      const taskPayload = buildTaskPayload(task);
      switch (section) {
        case TASK_SECTION.SETTINGS:
          response = await updateTaskSettings(task.id, {
            settings: taskPayload.settings,
          });
          break;
        case TASK_SECTION.SOLUTION:
          response = await updateTaskSolution(task.id, {
            solution: taskPayload.solution,
          });
          break;
        case TASK_SECTION.QUESTION:
          response = await updateTaskQuestion(task.id, {
            question: taskPayload.question,
          });
          break;
        case TASK_SECTION.ANSWER:
          response = await updateTaskAnswers(task.id, {
            answer: taskPayload.answer,
          });
          break;
        case TASK_SECTION.RECOMMENDATIONS:
          response = await updateTaskRecommendations(
            task.id,
            { recommendations: taskPayload.recommendations }
          );
          break;
        default:
          return;
      }
      if (response?.status === 200) {
        showNotification(
          "success",
          response.message || "Saved.",
          enqueueSnackbar,
          closeSnackbar
        );
        afterEdit(getTasks, populateTask, setOriginal, task, setEditTask);
      }
    } catch (e) {
      // Errors surfaced by http helpers / Swal
    } finally {
      setSubmittingSection(null);
    }
  };

  const handleVerify = () => {
    if (!editTask?.id) return;
    verfiyTask(editTask.id)
      .then((res) => {
        if ([200, 201].includes(res.status)) {
          showNotification(
            "success",
            res.message,
            enqueueSnackbar,
            closeSnackbar
          );
        } else {
          showWarningSwal(res.message);
        }
      })
      .catch(() => {})
      .finally(() => {
        afterEdit(getTasks, populateTask, setOriginal, editTask, setEditTask);
        setConfirmVerify(false);
      });
  };

  if (!isAdd) {
    if (loading) return <Loader height={600} />;
    if (!editTask && firstLoaded) return <NoData height={600} />;
    if (!editTask) return null;
  }

  return (
    <>
      {tasks.map((task, index) => (
        <TaskPanel
          key={isAdd ? index : task.id}
          task={task}
          setTask={(newTask) => setTaskAtIndex(index, newTask)}
          taskIndex={index}
          isAdd={isAdd}
          isReadOnly={isReadOnly}
          onAddTask={handleAddTask}
          onDeleteTask={handleDeleteTask}
          onDuplicateTask={handleDuplicateTask}
          original={original}
          editAnswers={editAnswers}
          submittingSection={submittingSection}
          onSubmitSection={onSubmitSection}
        />
      ))}

      {!isReadOnly && isAdd && (
        <Grid container justifyContent="flex-end" style={{ marginBlock: 20 }}>
          <ReusableButton
            text="Submit"
            bgColor="#5E75C3"
            fontSize={16}
            onClick={onSubmit}
            disabledButton={submitting}
          />
        </Grid>
      )}

      {isVerify && original && (
        <Grid
          container
          justifyContent="flex-end"
          style={{ margin: "0 30px 30px 30px" }}
        >
          <ReusableButton
            text={original.status === "Verified" ? "verified" : "verify"}
            bgColor={original.status === "Verified" ? "#999" : "#5E75C3"}
            fontSize={16}
            onClick={() =>
              original.status === "Verified"
                ? undefined
                : setConfirmVerify(true)
            }
          />
        </Grid>
      )}

      {isVerify && (
        <Modal open={confirmVerify} onClose={() => setConfirmVerify(false)}>
          <ConfirmVerfiy
            confirmFunction={handleVerify}
            cancelConfirm={() => setConfirmVerify(false)}
            object="task"
            value={editTask}
          />
        </Modal>
      )}
    </>
  );
}
